import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, FileText, Film, Share2, Eye } from "lucide-react";
import api from "../lib/api";
import { BLOG_COUNT_WORDS } from "../lib/visibleBlogs";
import { useLang, BLOG_INFO } from "../context/LangContext";
import useDocumentTitle from "../hooks/useDocumentTitle";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BlogBadge from "../components/BlogBadge";

const HERO = {
  suica: "https://images.unsplash.com/photo-1747574172799-5ef1d0fcd8f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  desenvolvimento: "https://images.unsplash.com/photo-1607799279861-4dd421887fb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  automacao: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const TOPICS = { suica: ["vida na Suíça", "Swiss life"], desenvolvimento: ["desenvolvimento de software", "software development"], automacao: ["automação", "automation"] };

function heroSubtitle(ids, lang) {
  const list = (ids.length ? ids : Object.keys(TOPICS)).map((id) => TOPICS[id][lang === "pt" ? 0 : 1]);
  const joined = list.length > 1 ? `${list.slice(0, -1).join(", ")} ${lang === "pt" ? "e" : "and"} ${list[list.length - 1]}` : list[0];
  const cap = joined.charAt(0).toUpperCase() + joined.slice(1);
  const n = ids.length || 3;
  const words = (BLOG_COUNT_WORDS[n] || BLOG_COUNT_WORDS[3])[lang === "pt" ? 0 : 1].toLowerCase();
  return lang === "pt"
    ? `${cap} — guias práticos, tutoriais e resultados das minhas experiências, ${n === 1 ? "num blog independente" : `reunidos em ${words} independentes`}.`
    : `${cap} — practical guides, tutorials and results from my own experiments, ${n === 1 ? "in one independent blog" : `gathered in ${words.replace(" blogs", "")} independent blogs`}.`;
}

export default function HomePage() {
  const { t, lang } = useLang();
  useDocumentTitle(
    t("ReiMultiBlog — Três blogs, um motor de conteúdo", "ReiMultiBlog — Three blogs, one content engine"),
    t("Suíça, Desenvolvimento e Automação: guias práticos, tutoriais e resultados das minhas experiências em três blogs independentes.", "Swiss life, Development and Automation: practical guides, tutorials and results from my own experiments across three independent blogs.")
  );
  const [blogs, setBlogs] = useState([]);
  const [stats, setStats] = useState({ articles: 0, videos: 0, social_posts: 0 });
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/public/blogs").then((r) => setBlogs(r.data)).catch(() => {});
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/articles?page_size=6").then((r) => setArticles((r.data.items || r.data).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="App relative grain">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-muted-foreground animate-fade-up">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            {t("Guias práticos e experiências em primeira mão", "Practical guides & first-hand experiments")}
          </div>
          <h1 className="mt-6 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] animate-fade-up" style={{ animationDelay: "60ms" }}>
            {t(`${(BLOG_COUNT_WORDS[blogs.length] || BLOG_COUNT_WORDS[3])[0]}. Um motor de conteúdo.`, `${(BLOG_COUNT_WORDS[blogs.length] || BLOG_COUNT_WORDS[3])[1]}. One content engine.`)}
            <span className="block brand-gradient">ReiMultiBlog</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-muted-foreground animate-fade-up" style={{ animationDelay: "120ms" }}>
            {heroSubtitle(blogs.map((b) => b.id), lang)}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6 animate-fade-up" style={{ animationDelay: "180ms" }}>
            {[
              { icon: FileText, n: stats.articles, l: t("Artigos", "Articles") },
              { icon: Film, n: stats.videos, l: t("Vídeos", "Videos") },
              { icon: Share2, n: stats.social_posts, l: t("Posts sociais", "Social posts") },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/5 border border-white/10"><s.icon className="h-5 w-5 text-primary" /></span>
                <div>
                  <div className="text-2xl font-extrabold font-heading leading-none">{s.n}</div>
                  <div className="text-xs text-muted-foreground">{s.l}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/2 hidden lg:block opacity-30" style={{ background: "radial-gradient(600px circle at 70% 30%, rgba(59,130,246,0.25), transparent 60%)" }} />
      </section>

      {/* Blog cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid gap-6 lg:grid-cols-3">
          {blogs.map((b, i) => (
            <Link
              key={b.id}
              to={`/blog/${b.id}`}
              data-testid={`home-blog-card-${b.id}`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-card animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="relative h-44 overflow-hidden">
                <img src={HERO[b.id]} alt={lang === "pt" ? b.title_pt : b.title_en} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent, ${BLOG_INFO[b.id]?.color}22 80%, rgba(0,0,0,0.7))` }} />
                <div className="absolute top-3 left-3"><BlogBadge blog={b.id} /></div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-bold">{lang === "pt" ? b.title_pt : b.title_en}</h3>
                <p className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                  <span>{b.article_count} {t("artigos", "articles")}</span>
                  <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{b.views || 0}</span>
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: BLOG_INFO[b.id]?.color }}>
                  {t("Explorar", "Explore")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Latest articles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6">{t("Últimos artigos", "Latest articles")}</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {articles.map((a) => (
            <Link key={a.id} to={`/blog/${a.blog}/${a.slug}`} data-testid={`home-article-${a.id}`} className="group rounded-2xl border border-white/10 bg-card overflow-hidden animate-fade-up">
              {a.images?.hero && <img src={a.images.hero} alt={a.title} className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
              <div className="p-5">
                <BlogBadge blog={a.blog} />
                <h3 className="mt-3 font-heading text-lg font-semibold leading-snug group-hover:text-primary transition-colors">{a.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
